/**
 * Helpers for agency-scoped async requests.
 *
 * Agency switches abort in-flight requests via `agencyAbortController`.
 * Request layers often return `null`/`undefined` on cancel instead of throwing.
 * Operations must clear `loading` in that case or charts stay stuck until refresh.
 */

export const isRequestCanceled = (error) =>
    error?.name === 'CanceledError'
    || error?.name === 'AbortError'
    || error?.code === 'ERR_CANCELED';

/**
 * Creates a monotonic generation tracker to prevent stale responses from
 * dispatching over fresher data.
 *
 * Usage:
 *   const tracker = createRequestGenerationTracker();
 *   // At operation start:
 *   const gen = tracker.next();
 *   // Pass into complete/fail:
 *   isCurrent: () => tracker.isCurrent(gen)
 *
 * @returns {{ next: () => number, isCurrent: (gen: number) => boolean }}
 */
export const createRequestGenerationTracker = () => {
    let generation = 0;
    return {
        next() {
            generation += 1;
            return generation;
        },
        isCurrent(gen) {
            return gen === generation;
        },
    };
};

/**
 * Dispatch success, or clear loading without an error UI when the request was aborted.
 *
 * @param {Object} options
 * @param {Function} options.dispatch
 * @param {AbortSignal} [options.signal]
 * @param {*} options.response
 * @param {Function} options.successAction
 * @param {Function} options.errorAction - ERROR actions set loading=false; empty payload hides error UI
 * @param {Function} [options.mapSuccess]
 * @param {Function} [options.isCurrent] - Returns false when a newer request has been issued
 */
export const completeAgencyScopedRequest = ({
    dispatch,
    signal,
    response,
    successAction,
    errorAction,
    mapSuccess,
    isCurrent,
}) => {
    if (isCurrent && !isCurrent()) {
        return;
    }

    if (signal?.aborted || response == null) {
        dispatch(errorAction(''));
        return;
    }

    dispatch(successAction(mapSuccess ? mapSuccess(response) : response));
};

/**
 * @param {Object} options
 * @param {Function} options.dispatch
 * @param {AbortSignal} [options.signal]
 * @param {*} options.error
 * @param {Function} options.errorAction
 * @param {Function} [options.isCurrent] - Returns false when a newer request has been issued
 */
export const failAgencyScopedRequest = ({
    dispatch,
    signal,
    error,
    errorAction,
    isCurrent,
}) => {
    if (isCurrent && !isCurrent()) {
        return;
    }

    if (signal?.aborted || isRequestCanceled(error)) {
        dispatch(errorAction(''));
        return;
    }

    const message = typeof error === 'string'
        ? error
        : String(error?.message || error || 'Request failed');

    dispatch(errorAction(message));
};
